import React, { useCallback, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import ToggleButton from '@components/common/ToggleButton/ToggleButton.component'
import WorkingDayBadge from '@components/common/WorkingDayBadge/WorkingDayBadge.component'
import { days, times } from '@constants/AddLocation/AddLocation.constants'
import { ClinicDetails, Day, DaySchedule } from '@dataTypes/location.type'

type WorkingHoursProps = {
  formData: ClinicDetails
  handleChange: (
    field: string,
    value: string | boolean | number | DaySchedule,
  ) => void
}
const WorkingHours: React.FC<WorkingHoursProps> = ({
  formData,
  handleChange,
}) => {
  const [selectedDay, setSelectedDay] = useState<Day | null>(null)
  const [startWH, setStartWH] = useState('8:00 AM')
  const [endWH, setEndWH] = useState('5:00 PM')

  // Function to update all open days with the same start and end time
  const updateAllDaysWithSameTime = (
    startTime: string,
    endTime: string,
  ): void => {
    days.forEach((day) => {
      if (formData[day as Day]?.isOpen) {
        handleChange(day, {
          ...formData[day as Day],
          startTime,
          endTime,
        })
      }
    })
  }

  const handleSameWorkHoursChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const isSameWorkHours = e.target.checked

    handleChange('sameWH', isSameWorkHours)

    if (isSameWorkHours) {
      // If sameWH is true, update all open days to default times
      updateAllDaysWithSameTime('8:00 AM', '5:00 PM')
    }
  }

  const handleTimeChange = (
    type: 'startTime' | 'endTime',
    value: string,
  ): void => {
    if (formData.sameWH) {
      // Apply the same start or end time to all days with isOpen: true
      updateAllDaysWithSameTime(
        type === 'startTime' ? value : startWH,
        type === 'endTime' ? value : endWH,
      )
    } else if (selectedDay) {
      // Only change the selected day if sameWH is false
      handleChange(selectedDay, {
        ...formData[selectedDay],
        [type]: value,
      })
    }
  }

  const handleDayClick = useCallback(
    (day: Day): void => {
      setSelectedDay(day)
      setStartWH(formData[day]?.startTime || '')
      setEndWH(formData[day]?.endTime || '')
      const isOpen = !formData[day]?.isOpen

      handleChange(day, {
        ...formData[day],
        isOpen: isOpen,
        ...(formData.sameWH && isOpen
          ? { startTime: startWH, endTime: endWH }
          : {}),
      })
    },
    [formData, handleChange, startWH, endWH],
  )

  const renderWorkingDays = useCallback((): JSX.Element => {
    const displayLetters = ['M', 'T', 'W', 'T', 'F', 'S', 'S']

    return (
      <div className="modal-working-days-container">
        {days.map((day, index) => (
          <WorkingDayBadge
            key={index}
            day={displayLetters[index]}
            isAvailable={formData[day as Day]?.isOpen}
            onClick={() => handleDayClick(day as Day)}
          />
        ))}
      </div>
    )
  }, [handleDayClick, formData])

  return (
    <Form.Group className="">
      <Form.Label className="mb-2">Working days</Form.Label>
      <div className="working-hours-container mb-2">
        {formData.sameWH ? (
          <Row>
            <Col md={6}>
              <Form.Group className="">
                <Form.Label className="working-hours-label">
                  Working Days
                </Form.Label>
              </Form.Group>
              <div>{renderWorkingDays()}</div>
            </Col>
            <Col md={6}>
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="working-hours-label">
                      Opens at
                    </Form.Label>
                    <Form.Select
                      defaultValue={startWH}
                      onChange={(e) => {
                        setStartWH(e.target.value)
                        handleTimeChange('startTime', e.target.value)
                      }}
                    >
                      {times.map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="working-hours-label">
                      Closes at
                    </Form.Label>
                    <Form.Select
                      defaultValue={endWH}
                      onChange={(e) => {
                        setEndWH(e.target.value)
                        handleTimeChange('endTime', e.target.value)
                      }}
                    >
                      {times.map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Col>
          </Row>
        ) : (
          <>
            {days.map((day, index) => (
              <Row key={index}>
                <Col md={6} key={`switch-${day}`}>
                  <Form.Group className="mb-2">
                    <Form.Label className="working-hours-label">
                      {day}
                    </Form.Label>
                    <ToggleButton
                      key={day}
                      id={`switch-${day}`}
                      type="switch"
                      name={``}
                      size="md"
                      checked={formData[day as Day]?.isOpen}
                      onChange={(e) =>
                        handleChange(day as string, {
                          ...formData[day as Day], // Spread the previous day's data
                          isOpen: e.target.checked, // Update the isOpen property
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6} key={`time-${day}`}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label className="working-hours-label">
                          Start
                        </Form.Label>
                        <Form.Select
                          defaultValue={formData[day as Day]?.startTime}
                          disabled={!formData[day as Day]?.isOpen}
                          onChange={(e) =>
                            handleChange(day as string, {
                              ...formData[day as Day],
                              startTime: e.target.value,
                            })
                          }
                        >
                          {times.map((time) => (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label className="working-hours-label">
                          End
                        </Form.Label>
                        <Form.Select
                          defaultValue={formData[day as Day]?.endTime}
                          disabled={!formData[day as Day]?.isOpen}
                          onChange={(e) =>
                            handleChange(day as string, {
                              ...formData[day as Day],
                              endTime: e.target.value,
                            })
                          }
                        >
                          {times.map((time) => (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                </Col>
              </Row>
            ))}
          </>
        )}
      </div>
      <ToggleButton
        key={'same-working-hours'}
        id={`same-working-hours`}
        type="checkbox"
        name={`same-working-hours`}
        size="md"
        checked={formData.sameWH}
        label={'Same Working Hours'}
        onChange={handleSameWorkHoursChange}
      />
    </Form.Group>
  )
}

export default WorkingHours
